import MggpPlayer from 'mggp-player';
import { TrackBuffering } from './track-buffering';
import { onVisibilityChange } from './on-visibility-change';
import { getAdDescription } from './get-ad-description';
import { getError } from "./get-error";

/**
 * @class MggpPluginMet
 */

/*
нам сейчас нужно просто снизить трафик до приемлемого
чуть позже будет какой-то механизм прокидывания конфигурации из трекера во время инита сессии
но это "потом" (с) Макеев
 */

const PLAYBACK_INTERVAL = 120;
const BUFFERING_EVENT_INTERVAL = 500;
const SEEK_DRIFT_TIME = 2;

export default class MggpPluginMet extends MggpPlayer.Plugin {
  constructor(...params) {
    super(...params);

    //IE fix
    if (!this.config && !this.player) {
      this.player = arguments[0];
      this.config = arguments[1];
    }

    this._createdTime = Date.now();
    this._playerStartSended = false;
    this._videoInfo = null;
    this._lastAdInfo = null;

    this._playbackTimer = 0;
    this._playbackTime = 0;
    this._seekStartedTime = 0;

    this._playerState = -1;
    this._lastPlayerState = -1;

    this._lastStreamPlayTime = 0;

    this._isStarted = false;
    this._isSeeking = false;
    this._isPaused = false;
    this._isBuffering = false;
    this._isQualityChanged = false;
    this._isSeekStarted = false;
    this._bufferingTimeout = 0;
    this._bufferingTime = BUFFERING_EVENT_INTERVAL;
    this._bufferingStartTime = 0;
    this._stopTimeout = 0;

    this._currentProgram = null;
    this._currentChannel = null;
    this._programChangedAfterClick = false;
    this._channelChangedAfterClick = false;

    this._fatalErrorSended = false;
    this._isLive = false;
    this._isDebug = this.config.metDebug;
    this._isSTV = this.config.deviceType === 'tv';

    this._init();

    this._dispatch('player_loading');
  }

  get _isHidden(){
    return document.visibilityState === 'hidden';
  }

  load(){
    if (this._playerStartSended) {
      return;
    }
    this._dispatch('player_start');
  }

  trackBuffering = new TrackBuffering({
    getInterval: (n) => (isNaN(n) ? BUFFERING_EVENT_INTERVAL : n * 2),
    construct: (...args) => this._construct(...args),
    dispatch: (eventName, payload) => {
      this.#dispatchStreamEvent(`buffering_${eventName}`, payload);
    },
  });

  _dispatch(pType, pData = this._construct()) {
    if (this._isDebug) {
      console.log(`dispatch event: ${pType}`);
      console.table(pData);
    }

    this.player.emit(`met:${pType}`, pData);
  }

  /**
   * In additional to other ET data, add current program data (if available) into target.
   *
   * @todo Explore: do we have to keep currentProgram inside plugin or may get from player at the moment when it needed?
   *
   * @param {"update"|"start"|"stop"|"seek"|"play"|"pause"|"resume"|"quality"} eventName
   * @param {object} [payload=this._construct()]
   */
  #dispatchStreamEvent(eventName, payload = this._construct()){
    if(this._videoInfo?.program) payload.target = Object.assign({}, payload.target, this._videoInfo.program);
    this._dispatch(`stream_${eventName}`, payload);
  }

  /**
   * @param {string} playerAction
   */
  #dispatchPlayerAction(playerAction){
    const payload = this._construct(playerAction);
    this._dispatch('player_action', payload);
  }

  _stop() {
    if (!this._isStarted) return;

    this.#dispatchStreamEvent('stop');

    clearInterval(this._playbackTimer);
    this._playerState = -1;
    this._playbackTime = 0; //MHP-1577
    this._isStarted = false;
    this.trackBuffering.stop();

    if (this._isSTV) {
      this._currentChannel = null;
    }
  }

  listeners = new Map([
    ['mediaError', () => undefined],
    [
      'met:seek',
      () => {
        if (!this._isSeeking && !this._isLive) {
          this.#dispatchStreamEvent('seek');
        }
        this._isSeeking = true;
      },
    ],

    /**
     * It seems this handler does not work, instead event emulated via direct call from load
     */
    ['player:start', () => this._dispatch('player_start')],
    ['met:player_start', () => this._playerStartSended = true],
    ['player:stop', this.onPlayerStop],
    ['met:player_stop', () => this._playerStartSended = false],
    ['player:buffering', this.onPlayerBuffering],

    ['property:videoInfo', this.onVideoInfoChange],
    ['property:state', this.onPlayerStateChange],
    ['property:epg:currentChannel', this.onEPGChannelChange],

    ['property:interactiveChannel:currentPlayingProgram', this.onTVProgramChange],
    ['property:epg:currentProgram', this.onTVProgramChange],

    ['interactive:ready', this.onInteractiveReady],
    ['interactive:error', this.onInteractiveError],
    ['interactive:preferences', this.onInteractivePreferences],

    ['dvrChannel:seek', this.onDvrChannelSeek],
    ['dvrChannel:backOnAir', this.onBackToAir],
    ['vodChannel:backOnAir', this.onBackToAir],
    ['liveDvr:backOnAir', this.onBackToAir],

    ['ad:external:vastEvent', this.onVASTEvent]
  ]);

  _init() {
    this.listeners.forEach((listener, event) => {
      this.player.on(event, listener.bind(this));
    });

    //MHP-2117: Если в плеер пихают заглушку и мы не играем контент, при этом мы _isStarted то очевидно нам нужен stop();
    this.player.on('player:messageMode', (e) => {
       if(e.data && this.player.get('state') < 1) this._stop(); // state === 0 or -1
    });

    this.player.on('skin:pauseClick', () => {
      this.#dispatchPlayerAction('PAUSE');
    });

    this.player.on('skin:playClick', () => {
      this.#dispatchPlayerAction('RESUME');
    });

    this.player.on('skin:seekClick', () => {
      if (this._isSeeking) {
        return;
      }
      this.#dispatchPlayerAction('SEEK');
      this._seekStartedTime = this._playbackTime;
      this._isSeekStarted = true;
    });

    this.player.on('skin:startOver', () => {
      this.#dispatchPlayerAction('RESTART');
    });

    this.player.on('skin:fullscreenStateChanged', () => {
      this.#dispatchPlayerAction('SCREENMODE');
    });

    this.player.on('skin:prevClick', () => {
      this.#dispatchPlayerAction('PREVIOUS_EPISODE');
    });

    this.player.on('skin:nextClick', () => {
      this.#dispatchPlayerAction('NEXT_EPISODE');
    });

    //TODO:Need add EPG_CHANNEL, EPG_PROGRAM events

    this.player.on('skin:prevChannelClick', () => {
      this._channelChangedAfterClick = true;
      this.#dispatchPlayerAction('PREVIOUS_CHANNEL');
    });

    this.player.on('skin:nextChannelClick', () => {
      this._channelChangedAfterClick = true;
      this.#dispatchPlayerAction('NEXT_CHANNEL');
    });

    this.player.on('skin:prevProgramClick', () => {
      this._programChangedAfterClick = true;
      this.#dispatchPlayerAction('PREVIOUS_CATCHUP');
    });

    this.player.on('skin:nextProgramClick', () => {
      this._programChangedAfterClick = true;
      this.#dispatchPlayerAction('NEXT_CATCHUP');
    });

    this.player.on('epg:programButtonClick', ({data} = {}) => {
      this._programChangedAfterClick = true;
      this.#dispatchPlayerActionWithExtraData('EPG_PROGRAM', data);
    });

    this.player.on('epg:show', () => {
      this.#dispatchPlayerAction('EPG_SHOW');
    });

    this.player.on('epg:epgListFocus', ({data} = {}) => {
      this.#dispatchPlayerAction('epg_' + data + '_focus');
    });

    this.player.on('epg:channelListUp', () => {
      this.#dispatchPlayerAction('EPG_CHANNELS_LIST_UP');
    });

    this.player.on('epg:channelListDown', () => {
      this.#dispatchPlayerAction('EPG_CHANNELS_LIST_DOWN');
    });

    this.player.on('epg:programListUp', () => {
      this.#dispatchPlayerAction('EPG_PROGRAM_LIST_UP');
    });

    this.player.on('epg:programListDown', () => {
      this.#dispatchPlayerAction('EPG_PROGRAM_LIST_DOWN');
    });

    this.player.on('epg:genreListUp', () => {
      this.#dispatchPlayerAction('EPG_GENRES_LIST_UP');
    });

    this.player.on('epg:genreListDown', () => {
      this.#dispatchPlayerAction('EPG_GENRES_LIST_DOWN');
    });

    this.player.on('epg:miniPlayer', () => {
      this.#dispatchPlayerAction('EPG_MINI_PLAYER');
    });

    this.player.on('epg:genreButtonClick', ({data} = {}) => {
      this.#dispatchPlayerActionWithExtraData('epg_genre', data);
    });

    this.player.on('skin:dispatchAction', ({data} = {}) => {
      const { actionName, actionData } = data;

      if (!actionName) return;

      if (actionData) return this.#dispatchPlayerActionWithExtraData(actionName, actionData);
      this.#dispatchPlayerAction(actionName);
    });

    this.player.on('skin:reportAnError', ({data} = {}) => {
      this.#dispatchPlayerActionWithExtraData('REPORT_AN_ERROR', data);
    });

    this.player.on('skin:recommendedButtonClick', () => {
      this.#dispatchPlayerAction('TO_BACKGROUND_MODE');
    });

    this.player.on('epg:channelButtonClick', ({data} = {}) => {
      this.#dispatchPlayerActionWithExtraData('epg_channel', data);
    });

    this.player.on('property:volume', () => {
      if (this._playbackTime) {
        this.#dispatchPlayerAction('SOUNDVOLUME');
      }
    });

    this.player.on('property:muted', () => {
      if (this._playbackTime) {
        this.#dispatchPlayerAction('SOUNDVOLUME');
      }
    });

    this.player.on('property:subtitles', () => {
      if (this._playbackTime) {
        this.#dispatchPlayerAction('SUBTITLE');
      }
    });

    this.player.on('property:audioTrack', () => {
      if (this._playbackTime) {
        this.#dispatchPlayerAction('AUDIOTRACK');
      }
    });

    this.player.on('property:quality', () => {
      if (this._playbackTime) {
        this.#dispatchPlayerAction('QUALITY');
        this._isQualityChanged = true;
      }
    });

    this.player.on('skin:recentChannelsShow', () => {
      this.#dispatchPlayerAction('RECENT_CHANNELS_SHOW');
    });
    this.player.on('skin:channelsShow', () => {
      this.#dispatchPlayerAction('CHANNELS_SHOW');
    });
    this.player.on('skin:channelsUp', () => {
      this.#dispatchPlayerAction('CHANNELS_UP');
    });
    this.player.on('skin:channelsDown', () => {
      this.#dispatchPlayerAction('CHANNELS_DOWN');
    });
    this.player.on('skin:trailerToWatch', () => {
      this.#dispatchPlayerAction('TRAILER_TO_WATCH');
    });

    this.player.on('skin:onboardingInfoClick', () => {
      this.#dispatchPlayerAction('ONBOARDING_SHOW');
    });


    this.player.on('skin:recentChannelsSelected', ({data} = {}) => {
      this.#dispatchPlayerActionWithExtraData('RECENT_CHANNELS_SELECTED', data);
    });

    this.player.on('property:pip:isPipModeOn', ({ data }) => {
      this._dispatch('change_mode', { mode: { type: 'pip', status: data } });
    });

    this.player.on("pip:error", ({ data }) => {
      const payload = { ...this._construct(), error: { code: "pip", info: data } };
      this._dispatch("player_error", payload);
    });

    this.player.on("player:playerError", ({ name, data }) => {
      const error = getError(this._videoInfo, data);
      if(!error) return;

      const payload = { ...this._construct(), error: error };
      this._dispatch("player_error", payload, name);
    });

    onVisibilityChange(
      !this._isSTV,
      () => this.#dispatchPlayerAction('INVISIBLE')
    );

    this._inited = true;
  }

  onPlayerStateChange(e) {
    this._lastPlayerState = Number(this._playerState);
    this._playerState = e.data;

    let video_id = this.player.get('videoInfo').video_id;
    if (!video_id) {
      return;
    }

    switch (this._playerState) {
      case 0: {
        //MHP-1759
        this._stop();
        break;
      }
      case 1: {
        if (!this._isStarted) {
          this.#dispatchStreamEvent('start');
          this._isStarted = true;

          this._playbackTimer = setInterval(() => {
            if (this._playerState === 1) {
              this._playbackTime += 0.5;
            }
            if (this._playbackTime) {
              let seekTimeDif = this._playbackTime - this._seekStartedTime;
              if((seekTimeDif > SEEK_DRIFT_TIME || seekTimeDif < -SEEK_DRIFT_TIME) && this._isSeekStarted) {
                this._seekStartedTime = 0;
                this._isSeekStarted = false;
              }
              if (
                this._lastStreamPlayTime !== this._playbackTime &&
                this._playbackTime % PLAYBACK_INTERVAL === 0
              ) {
                this._lastStreamPlayTime = this._playbackTime;
                this.#dispatchStreamEvent('play');
              }
            }
          }, 500);
        } else {
          if (this._isPaused && !this._isHidden) {
            this.#dispatchStreamEvent('resume');
          } else if (this._isSeeking && this._playbackTime) {
            this._isSeeking = false;
            if (!this._isHidden) {
              this.#dispatchStreamEvent('resume');
            }
          }
        }
        if (this._isQualityChanged) {
          this._isQualityChanged = false;
          this.#dispatchStreamEvent('quality');
        }

        this._isPaused = false;

        break;
      }
      case 2: {
        if (this._isStarted && !this._isPaused) {
          this.#dispatchStreamEvent('pause');
          this._isPaused = true;
        }
        break;
      }
      case 4: {
        if(this._forceSeekEvent()) this._isSeekStarted = true;
        if (this._isSeekStarted && !this._isSeeking && this._playbackTime > 0 && !this._isHidden) {
          this._isSeeking = true;
          this._isSeekStarted = false;
          this.#dispatchStreamEvent('seek');
        }
        break;
      }
      case 5: {
        //Стейт подвешенный, мы и не на паузе и не играем, но и не в перемотке
        if (this._isPaused) {
          this._isSeeking = false;
        }
        break;
      }
    }
  }

  onVideoInfoChange(e) {
    let videoInfo = e.data;
    if (!videoInfo) {
      return;
    }
    if (this._stopTimeout) {
      clearTimeout(this._stopTimeout);
    }

    if (
      !videoInfo['virtual_id'] &&
      this._videoInfo &&
      videoInfo['video_id'] === this._videoInfo['video_id'] &&
      videoInfo['content_type'] === this._videoInfo['content_type'] //MHP-1450
    ) {
      return;
    }

    if (
      this._videoInfo &&
      this._videoInfo['virtual_id'] &&
      videoInfo['virtual_id'] === this._videoInfo['virtual_id']
    ) {
      return;
    }

    this._stop();
    this._videoInfo = this._copy(videoInfo);
    this._playbackTime = 0;
    this._isStarted = false;
    this._isVideoInfoChanged = true;
    if(this._currentChannel && !this._videoInfo.channel_id) {
      this._videoInfo.channel_id = parseInt(this._currentChannel.id);
    }
    this.concatProgramToVideoInfo(this._currentProgram, this._videoInfo);

    let contentType = videoInfo.content_type;
    if (contentType) {
      this._isLive =
        contentType === 'tv_dvr' ||
        contentType === 'tv_linear' ||
        contentType.indexOf('live') !== -1;
    }
    clearInterval(this._playbackTimer);
  }

  /**
   * Validate applying predicates, check isBuffering,
   * call start/stop respectively.
   * predicates:
   *   - non-empty event payload
   *   - player/document is visible (?)
   *   - stream as started
   *   - playback time greater than 10 sec.
   *   - is not seeking
   *
   * @param {{data: BufferingEventPayload}} e
   * @this MggpPluginMet
   */
  onPlayerBuffering (e) {
    const event = e.data;
    const predicates = [
      Boolean(event),
      false === this._isHidden, // but why?!
      this._isStarted,
      this._playbackTime > 10,
      false === event.isSeeking,
    ];

    if (false === predicates.every(Boolean)) {
      return;
    }

    if (event['isBuffering']) {
      /*
        TODO: need to investigate deeply,
          could we use `event.timestamp` as startAt instead Date.now?
      */
      this.trackBuffering.start();
    } else {
      this.trackBuffering.stop();
    }
  }

  /**
   * этот, и все другие обработчики событий интерактива создают
   * базовый ивент и используют метод extendEvent от плагина интерактива,
   * если какой параметр не соотвествует ожиданиям ET то скорре всего
   * исправления будут на стороне плагина интерактива.
   * Этот обработчк дополнительно валидирует/исправляет структуру поля error,
   * что бы соотвествовать требованиям ET
   * @param ev
   */
  onInteractiveError(ev) {
    let payload = this._construct();
    if(typeof ev.data?.extendEvent === "function"){
      payload = ev.data.extendEvent(payload);
    }

    payload.error ??= {};
    payload.error.info ??= '';
    payload.error.code ||= 'UNKNOWN';
    payload.error.code = payload.error.code.slice(0, 200);
    this._dispatch('interactive_error', payload);
  }

  /**
   * @param ev
   */
  onInteractiveReady(ev){
    let payload = this._construct('INTERACTIVE_START');
    if(typeof ev.data?.extendEvent === "function"){
      payload = ev.data.extendEvent(payload);
    }
    this._dispatch('player_action', payload);
  }

  onInteractivePreferences(ev){
    let payload = this._construct(ev.data.isEnable ? 'INTERACTIVE_ENABLE': 'INTERACTIVE_DISABLE');
    if(typeof ev.data?.extendEvent === "function"){
      payload = ev.data.extendEvent(payload);
    }
    this._dispatch('player_action', payload);
  }

  onVASTEvent(e) {
    let eventData = e.data;
    //MHP-1843: Listen only start event, but in vpaid it not guaranteed
    if (eventData['vastEvent'] === 'impression') {
      const metData = this._construct();
      const adData = getAdDescription(this.player.get('videoInfo'), eventData);
      Object.assign(metData, adData);
      this._lastAdInfo = eventData;
      this._dispatch('player_ad_start', metData);
    }
  }

  onEPGChannelChange(e) {
    if (!e.data) {
      return;
    }

    if (this._channelChangedAfterClick) {
      // dispatch this action before the 'this._stop', because it will clear the 'this._currentChannel'
      this._channelChangedAfterClick = false;
      this.#dispatchPlayerAction("EPG_CHANNEL");
    }

    this._stop();
    if (e.data._programs.length === 0){
      this._currentProgram = {};
    }

    this._currentChannel = e.data;

    if(this._currentChannel && this._videoInfo && !this._videoInfo.channel_id && this._getObjectType() === "TV") {
      this._videoInfo.channel_id = this._currentChannel.id;
    }
  }

  onPlayerStop() {
    /*if (this._isLive && this._isSeeking) {
      this.#dispatchStreamEvent('seek');
      return;
    }*/

    //this._stop();
  }

  //MHP-1697: эмуляция сика на двр канале, где сик это подмена src
  onDvrChannelSeek() {
    if(this._getObjectType() !== "TV" || this._isSeeking) return;
    this._isSeeking = true;
    this._isSeekStarted = false;
    this.#dispatchStreamEvent('seek');
  }

  onBackToAir(e) {
    // MHP-1483: Не могу сказать зачем но веб файрит backOnAir и просит начать сначала :(
    if (e && e.data && e.data['startFromZero']) {
      return;
    }
    this.#dispatchPlayerAction('BACK_ONAIR');
    this._getObjectType() === "TV" ? this.onDvrChannelSeek() : this._isSeekStarted = true ; //MHP-1697
  }

  onTVProgramChange (e) {
    let program = this.player.get('epg:currentProgram');
    let interactiveProgram = this.player.get('interactiveChannel:currentPlayingProgram');

    // MHP-1893
    // Специфика веба подразумевает что у нас есть программа в єфире и програма которая играет, смарты работают иначе
    // Возможно для смартов нужно будет потюнить эту часть

    if (interactiveProgram) {
      if (this._currentProgram && this._currentProgram.id === interactiveProgram.id) return;
      program = interactiveProgram;
    }

    if(this._currentProgram && program && program.id === this._currentProgram.id) {
      return;
    }

    this._currentProgram = program;
    if(!this._programChangedAfterClick) {
      this.concatProgramToVideoInfo(this._currentProgram, this._videoInfo);

      //MHP-1948: WEB
      const interactiveProgramChange = e.name === "property:interactiveChannel:currentPlayingProgram";
      const onAir = this.player.get('interactiveChannel:channelState') === "onAir";
      const isCatchup = this.player.get('interactiveChannel:channelState') === "catchup";
      const isLinearTv = this._videoInfo?.content_type === "tv_linear";

      //В некоторых случаях у нас может возникнуть stop после onEPGChannelChange и нужно вернуть данные про канал
      if(!this._currentChannel && this._currentProgram) this._currentChannel = this._currentProgram.channel;

      if (this._playbackTime > 0 && // не реагировать при старте
        this._getObjectType() === "TV" && // игнорировать М каналы, лайв и прочее
        // только для програмы в эфире или если мы были в таймшифте и дошли до конца, актуально для веб
        (onAir || (interactiveProgramChange && isCatchup) || (isLinearTv && this._currentProgram) ) &&
        this._currentChannel && this._currentProgram && this._currentChannel.id === this._currentProgram.channel.id // не реагировать на смену канала
      ) {
        this._lastStreamPlayTime = 0;
        this._playbackTime = 0;
        this.#dispatchStreamEvent('update');
      }
    }

    //Todo: MHP-1948: STV
    // Нужно или реализовать логику this.player.get('interactiveChannel:channelState') => "onAir"/"timeshift"/"catchup"/"idle"/null
    // или написать полностью свою реализацию

    this._programChangedAfterClick = false;
  }

  _construct(pPlayerAction) {
    let result = {};
    let targetInfo;

    //MHP-1557
    let videoInfo = this._copy(this._videoInfo || this.player.get('videoInfo') || {});
    if (videoInfo && !videoInfo['video_id']) {
      videoInfo = null;
    }

    let playerInfo = result.player = {
      session_duration: Math.round((Date.now() - this._createdTime) / 1000),
      version: process.env.VERSION,
      invisible: Number(this._isHidden),
    };

    if (pPlayerAction) {
      playerInfo['action'] = pPlayerAction;
    }

    //MHP-1476
    targetInfo = result.target = {};
    targetInfo['object_type'] = this._getObjectType();

    if (videoInfo) {
      targetInfo['object_id'] = videoInfo['video_id'];

      if (videoInfo['virtual_id']) {
        targetInfo['virtual_object_id'] = videoInfo['virtual_id'];
      } else { //MHP-1759 Если это не кечап и мы в рамках канала
        if (videoInfo['channel_id']) {
          targetInfo['object_id'] = videoInfo['channel_id'];
          //targetInfo['virtual_object_id'] = String(videoInfo['video_id']); //MHP-2037
        }
      }

      targetInfo['object_title'] = videoInfo.program ? videoInfo.program['object_title'] : videoInfo['title']; //MHP-1751

      if (this._currentChannel) {
        targetInfo['genre_id'] = getCategoryIdFromChannel(this._currentChannel);
      }

      if (this.player.get('wss:sessionId')) {
        playerInfo['wstat_session_id'] = this.player.get('wss:sessionId');
      }

      if (videoInfo['src']) {
        playerInfo['cdn_node_host'] = videoInfo.src; //MHP-1474
      }

      if (videoInfo['content_type']) {
        playerInfo['content_type'] = videoInfo['content_type'];
      }

      playerInfo['playback_duration'] = Math.round(this._playbackTime);
      playerInfo['screen_mode'] =
        this._isSTV ? 'fullscreen' : document['fullscreenElement'] ? 'fullscreen' : 'notfullscreen';

      if (videoInfo['drm_type']) {
        playerInfo['drm_type'] = videoInfo['drm_type'];
      }

      if (videoInfo['stream_type']) {
        playerInfo['stream_type'] = videoInfo['stream_type'];
      }
    }

    if (this.player.get('audioTrack')) {
      if(this.player.get('audioTrackList')) this.player.get('audioTrackList').forEach((audioTrack) => {
        if (audioTrack.id === this.player.get('audioTrack'))
          playerInfo['audiotrack_language'] = audioTrack.lang;
      });
    }

    if (this.player.get('subtitles') !== 'off') {
      if(this.player.get('subtitlesList')) this.player.get('subtitlesList').forEach((subtitle) => {
        if (subtitle['lang_tag'] === this.player.get('subtitles'))
          playerInfo['subtitle_language'] = subtitle['lang'];
      });
    }

    if (!this._isLive) {
      playerInfo['duration'] = this._getPlayerDuration() || null;
      playerInfo['timeline_position'] = parseInt(this.player.get('position')) || null;
    }
    playerInfo['sound_volume'] = this.player.get('muted')
      ? 0
      : parseInt(this.player.get('volume')) || null;
    playerInfo['quality'] = this.player.get('quality');

    this._validate(targetInfo);
    this._validate(playerInfo);

    return result;
  }

  _getPlayerDuration() {
    let duration = this.player.get('duration');
    /*https://html.spec.whatwg.org/multipage/media.html#dom-media-duration */
    /*If no media data is available, then the attributes must return the Not-a-Number (NaN) value.
      If the media resource is not known to be bounded (e.g. streaming radio, or a live event with no announced end time),
       then the attribute must return the positive Infinity value.
    */
    if (isNaN(duration) || duration > 1000000) return 0; //MHP-1181

    return parseInt(duration);
  }

  /*
  TV	tv, tv_dvr, tv_dvr_vod, tv_linear, tv_linear_vod
  VOD	vod
  MCHANNEL	m_ch, m_ch_vod
  CATCHUP	tv_dvr_catchup
  TRAILER	trailer
  LIVE	live
  */

  _getObjectType() {
    if (!this._videoInfo) return 'OTHER';
    let type;

    switch (this._videoInfo.content_type) {
      case 'vod':
        type = 'VOD';
        break;
      case 'tv':
      case 'tv_dvr':
      case 'tv_dvr_vod':
      case 'tv_linear':
      case 'tv_linear_vod':
        type = 'TV';
        break;
      case 'tv_dvr_catchup':
        type = 'CATCHUP';
        break;
      case 'm_ch':
      case 'm_ch_vod':
        type = 'MCHANNEL';
        break;
      case 'live':
      case 'live_dvr':
        type = this._videoInfo.video_wall ? 'TV_WALL' : 'LIVE';
        break;
      case 'trailer':
        type = this._currentProgram ? 'MCHANNEL' : 'TRAILER';
        break;
      default:
        type = 'OTHER';
    }
    return type;
  }

  _validate(data) {
    for (const key in data) {
      if(data[key] === undefined || data[key] === null || Number.isNaN(data[key])) {
        delete data[key];
      }
    }
    return data;
  }

  _copy(target) {
    let result = null;
    try {
      result = JSON.parse(JSON.stringify(target));
    } catch (e) {
      console.error(e);
    }

    return result;
  }

  //MHP-1697
  // В некоторых случаях у нас возможен сик вне сик бара, например IOS Fullscreen
  _forceSeekEvent() {
    return this.player.get("iOSFullscreen") === true;
  }

  // MHP-1893
  // Что-бы гарантированно слать stream с нужной программой я добавляю программу прям в стрим.
  // Для связи М каналов и Кечапов у нас есть video_id/vidtual_id
  // В остальних случаях приходится немного думать
  concatProgramToVideoInfo(program, videoInfo) {
    //MHP-1948
    let programId = program? program.external_id || program.raw?.external_id: null;
    const programInfo = {
      program_id: programId,
      schedule_id: program?.id,
      category_id: program?.category?.id,
      start_ts: program?.startTimestamp,
      end_ts: program?.endTimestamp,
      object_title: program?.title
    };

    if(program && videoInfo) {
      if(!['TV', 'MCHANNEL', 'CATCHUP'].includes(this._getObjectType())) return;

      const programObjectId = parseInt(program.objectId);
      const programVirtualId = program.virtualObjectID || program.virtualObjectId || null;

      switch (videoInfo.content_type) {
        case "m_ch":
          return
        case "tv_dvr_vod":
        case "tv_linear_vod":
        case "m_ch_vod":
          if(programObjectId === videoInfo.video_id) videoInfo.program = programInfo;
          if(!videoInfo.virtual_id && videoInfo.video_id) programInfo.virtual_object_id = String(videoInfo.video_id);
          return;
        case "tv_dvr_catchup":
          if(programVirtualId && programVirtualId === videoInfo.virtual_id) videoInfo.program = programInfo;
          return;
        default:
          if(program.channel && videoInfo.channel_id && program.channel.id !== videoInfo.channel_id) return;
          if(program.id) {
            //MHP-2037: [WEB] [SmartTV] Некоректна передача target_virtual_object_id на ТБ
            if(!videoInfo.virtual_id && program.virtualObjectId) programInfo.virtual_object_id = program.virtualObjectId;
            videoInfo.program = programInfo;
          }
      }
    }
  }

  #dispatchPlayerActionWithExtraData(action, extraData) {
    const payload = this._construct(action);
    if (extraData) {
      if (extraData.target) Object.assign(payload.target, extraData.target);
      if (extraData.player) Object.assign(payload.player, extraData.player);
    }
    this._dispatch('player_action', payload);
  }
}

function getCategoryIdFromChannel(channel) {
  let id;

  if (channel?.originalGenre?.id !== undefined) {
    id = channel.originalGenre.id;
  } else if (channel?.genreID !== undefined) {
    id = channel.genreID;
  }

  return id;
}

